import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [variantData, setVariantData] = useState({});
  const [hasBeenReturned, setHasBeenReturned] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`/api/orders/${id}`, {
          withCredentials: true,
        });
        setOrder(res.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch order details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

   useEffect(() => {
    const fetchReturnCheck = async () => {
      try {
        const res = await axios.get(`/api/orders/returns/check/${id}`, {
          withCredentials: true,
        });
        setHasBeenReturned(res.data.hasBeenReturned);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch return check details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReturnCheck();
  }, [id]);

  useEffect(() => {
    const fetchVariants = async () => {
      if (!order?.items) return;

      const variantMap = {};
      await Promise.all(
        order.items.map(async (item) => {
          if (item.productId && item.variantId) {
            try {
              const res = await axios.get(`/api/products/${item.productId}`);
              const product = res.data;
              const variant = product.variants.find(
                (v) => v._id === item.variantId
              );
              if (variant) {
                variantMap[item.variantId] = {
                  ...variant,
                  productName: product.name,
                };
              }
            } catch (err) {
              console.error(
                `Failed to fetch variant for ${item.productId}`,
                err
              );
            }
          }
        })
      );

      setVariantData(variantMap);
    };

    fetchVariants();
  }, [order]);

   const handleTrackShipment = async () => {
    setTrackingLoading(true);
    try {
      const res = await axios.get(`/api/orders/${id}/track`, {
        withCredentials: true,
      });
      setTrackingInfo(res.data.tracking);
      // Update order status if it changed
      if (res.data.order.status !== order.status) {
        setOrder({ ...order, status: res.data.order.status });
      }
    } catch (err) {
      console.error("Failed to track shipment:", err);
      alert("Failed to get tracking information");
    } finally {
      setTrackingLoading(false);
    }
   }

  if (loading) return <div className="text-center mt-5">Loading...</div>;
  if (error) return <div className="alert alert-danger mt-4">{error}</div>;

  return (
    <div className="container py-4 px-2">
      <h3>Order #{order._id}</h3>

      <div className="row mt-4">
        <div className="col-md-6">
          <div className="card mb-3">
            <div className="card-header fw-bold">Shipping Information</div>
            <div className="card-body">
              <p>
                <strong>Name:</strong> {order.shippingAddress.name}
              </p>
              <p>
                <strong>Address:</strong> {order.shippingAddress.address}
              </p>
              <p>
                <strong>City:</strong> {order.shippingAddress.city}
              </p>
              <p>
                <strong>Postal Code:</strong> {order.shippingAddress.postalCode}
              </p>
              <p>
                <strong>Country:</strong> {order.shippingAddress.country}
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card mb-3">
            <div className="card-header fw-bold">Order Status</div>
            <div className="card-body">
              <p>
                <strong>Status:</strong> {order.status}
              </p>
              {order.eta && (
                <p>
                  <strong>ETA:</strong> {order.eta}
                </p>
              )}
              {order.trackingUrl && (
                <p>
                  <strong>Tracking:</strong>{" "}
                  <a href={order.trackingUrl} target="_blank" rel="noreferrer">
                    Track Shipment
                  </a>
                </p>
              )}
              <p>
                <strong>Placed On:</strong>{" "}
                {new Date(order.createdAt).toLocaleString()}
              </p>

              {order.shipping && order.shipping.trackingNumbers && (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6 className="fw-bold mb-2">
                    <i className="bi bi-truck"></i> DPD Shipping
                  </h6>
                  <p className="mb-1">
                    <strong>Carrier:</strong> {order.shipping.carrier}
                  </p>
                  <p className="mb-1">
                    <strong>Service:</strong>{" "}
                    {order.shipping.service || "Standard Next Day"}
                  </p>
                  <p className="mb-2">
                    <strong>Tracking Number:</strong>{" "}
                    <code>{order.shipping.trackingNumbers[0]}</code>
                  </p>
                  <button
                    className="btn btn-sm btn-dark"
                    onClick={handleTrackShipment}
                    disabled={trackingLoading}
                  >
                    {trackingLoading ? "Loading..." : "Track Shipment"}
                  </button>
                </div>
              )}
              
              {trackingInfo && (
                <div className="mt-3 p-3 border rounded">
                  <h6 className="fw-bold mb-2">Live Tracking</h6>
                  <p className="mb-1">
                    <strong>Current Status:</strong> {trackingInfo.status}
                  </p>
                  {trackingInfo.location && (
                    <p className="mb-1">
                      <strong>Location:</strong> {trackingInfo.location}
                    </p>
                  )}
                  {trackingInfo.timestamp && (
                    <p className="mb-1">
                      <strong>Last Update:</strong>{" "}
                      {new Date(trackingInfo.timestamp).toLocaleString()}
                    </p>
                  )}
                  {trackingInfo.estimatedDelivery && (
                    <p className="mb-1">
                      <strong>Estimated Delivery:</strong>{" "}
                      {new Date(
                        trackingInfo.estimatedDelivery
                      ).toLocaleDateString()}
                    </p>
                  )}

                  {/* Show tracking events if available */}
                  {trackingInfo.events && trackingInfo.events.length > 0 && (
                    <div className="mt-3">
                      <h6 className="fw-bold">Tracking History:</h6>
                      <ul className="list-unstyled">
                        {trackingInfo.events.map((event, idx) => (
                          <li key={idx} className="mb-2 text-muted small">
                            <i className="bi bi-circle-fill text-primary me-2"></i>
                            <strong>{event.status}</strong> -{" "}
                            {new Date(event.timestamp).toLocaleString()}
                            {event.location && ` at ${event.location}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Show error if shipping failed */}
              {order.shipping && order.shipping.error && (
                <div className="alert alert-warning mt-3">
                  <small>
                    <i className="bi bi-exclamation-triangle"></i> Shipping
                    label pending. Our team is processing your order.
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card my-4">
        <div className="d-flex justify-content-between align-items-center m-2">
          <div className="fw-bold">Items</div>
         {order.status === "Delivered" && !hasBeenReturned && <button className="btn text-danger btn-sm mt-2" onClick={() => navigate(`/order/${order._id}/return`)}>
            Return An Item
          </button> }
        </div>
        <ul className="list-group list-group-flush">
          {order.items.map((item, idx) => {
            const variant = variantData[item.variantId];
            const price = variant?.price || item.price || 0;
            const color = variant?.color || item.color;
            const laceSize = variant?.laceSize || item.laceSize;
            return (
              <li
                key={idx}
                className="list-group-item d-flex justify-content-between align-items-center"
                onClick={() => navigate(`/product/${item.productId}`)}
              >
                <div className="d-flex align-items-center">
                  <img
                    src={variant?.media}
                    alt={variant?.productName || "Product"}
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "cover",
                    }}
                    className="me-3"
                  />
                  <div>
                    <strong>{variant?.productName || "Product"}</strong>
                    <div className="text-muted small">
                      {variant?.length}" {variant?.texture} - {variant?.origin}
                      {color ? ` | Color: ${color}` : ""}
                      {laceSize ? ` | Lace Size: ${laceSize}` : ""}
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  {item.quantity} × £{price.toLocaleString()}{" "}
                  <br />
                  <strong>
                    £{(item.quantity * price).toLocaleString()}
                  </strong>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default OrderDetails;
